/** v1 → v2 for the records that are not commands, events or the snapshot (decision 0036, spec §2.12). */
import type { WakeBatch, WakeReason } from "../domain/agentlife"
import type { Artifact, Comment, MemoryItem, MemoryScope } from "../domain/knowledge"
import type { Agent, Area } from "../domain/org"
import type { Goal } from "../domain/plan"
import type { Project } from "../domain/workspace"
import type { ContextPack, LoadedItem } from "../job"
import type * as V1 from "../legacy/v1/index"
import type { Run, RunOutcome, RunState } from "../run"
import {
  fromV1Actor,
  fromV1EntityRef,
  fromV1Meta,
  fromV1Mind,
  fromV1NodeRef,
  fromV1Placement,
  fromV1Unmet,
  itemId,
} from "./common"
import { fromV1Blocker } from "./item"

type V1Run = V1.Run
type V1RunOutcome = V1.RunOutcome
type V1RunState = typeof V1.RunState.Type

export const fromV1RunOutcome = (o: V1RunOutcome): RunOutcome => {
  switch (o._tag) {
    case "blocked":
      return { _tag: "blocked", summary: o.summary, blocker: fromV1Blocker(o.blocker) }
    case "done":
    case "progress":
    case "question":
    case "impossible":
    case "unreported":
      return o
    default:
      return o satisfies never
  }
}

const fromV1RunState = (s: V1RunState): typeof RunState.Type => {
  switch (s._tag) {
    case "waiting":
      return { _tag: "waiting", unmet: s.unmet.map(fromV1Unmet), since: s.since }
    case "requested":
    case "leased":
    case "ended":
      return s
    default:
      return s satisfies never
  }
}

/** v1 Run → v2: `taskId` → `itemId`, attribution `initiativeId`/`taskId` → `topItemId`/`itemId`; the rest copied. */
export const fromV1Run = (r: V1Run): Run => {
  const { taskId, attribution, placement, state, claimed, outcome, ...rest } = r
  return {
    ...rest,
    itemId: taskId === null ? null : itemId(taskId),
    attribution: {
      goalIds: attribution.goalIds,
      topItemId: attribution.initiativeId === null ? null : itemId(attribution.initiativeId),
      areaId: attribution.areaId,
      itemId: attribution.taskId === null ? null : itemId(attribution.taskId),
      agentId: attribution.agentId,
    },
    placement: fromV1Placement(placement),
    state: fromV1RunState(state),
    claimed: claimed === null ? null : fromV1RunOutcome(claimed),
    outcome: outcome === null ? null : fromV1RunOutcome(outcome),
  }
}

export const fromV1MemoryScope = (s: V1.MemoryScope): MemoryScope => {
  switch (s._tag) {
    case "task":
      return { _tag: "item", projectId: s.projectId, itemId: itemId(s.taskId) }
    case "user":
    case "workspace":
    case "project":
    case "area":
    case "agent":
      return s
    default:
      return s satisfies never
  }
}

type V1MemoryContent = typeof V1.MemoryContent.Type
type V1MemoryDraft = typeof V1.MemoryDraft.Type

const fromV1MemoryContent = (c: V1MemoryContent): MemoryItem["content"] => {
  switch (c._tag) {
    case "decision":
      return {
        _tag: "decision",
        decidedAt: c.decidedAt,
        links: c.links.map(fromV1NodeRef),
        alternatives: c.alternatives,
      }
    case "rule":
    case "preference":
    case "fact":
    case "learning":
    case "playbook":
    case "journal":
      return c
    default:
      return c satisfies never
  }
}

export const fromV1MemoryDraft = (
  d: V1MemoryDraft,
): Pick<MemoryItem, "scope" | "title" | "body" | "content" | "triggers" | "locked"> => ({
  scope: fromV1MemoryScope(d.scope),
  title: d.title,
  body: d.body,
  content: fromV1MemoryContent(d.content),
  triggers: d.triggers,
  locked: d.locked,
})

export const fromV1MemoryItem = (m: V1.MemoryItem): MemoryItem => ({
  ...fromV1Meta(m),
  scope: fromV1MemoryScope(m.scope),
  content: fromV1MemoryContent(m.content),
  reviewedBy: m.reviewedBy === null ? null : fromV1Actor(m.reviewedBy),
})

export const fromV1Comment = (c: typeof V1.Comment.Type): Comment => ({
  ...fromV1Meta(c),
  target: fromV1EntityRef(c.target),
})

export const fromV1Artifact = (a: typeof V1.Artifact.Type): Artifact => {
  const { taskId, ...rest } = fromV1Meta(a)
  return { ...rest, itemId: taskId === null ? null : itemId(taskId) }
}

/** v1 GoalSpec → `target` / `criteria`: a metric goal keeps its target; a milestone is judged by a person. */
export const fromV1GoalSpec = (spec: typeof V1.GoalSpec.Type): Pick<Goal, "target" | "criteria"> =>
  spec._tag === "metric"
    ? { target: spec.target, criteria: [] }
    : { target: null, criteria: [...spec.definitionOfDone] }

export const fromV1Goal = (g: typeof V1.Goal.Type): Goal => {
  const { spec, ...rest } = fromV1Meta(g)
  return { ...rest, ...fromV1GoalSpec(spec) }
}

/** `wip.maxActiveInitiatives` → `wip.maxNow`. */
export const fromV1Wip = (w: { readonly maxActiveInitiatives: number }): { readonly maxNow: number } => ({
  maxNow: w.maxActiveInitiatives,
})

export const fromV1Area = (a: typeof V1.Area.Type): Area => ({ ...fromV1Meta(a), wip: fromV1Wip(a.wip) })

export const fromV1Project = (p: typeof V1.Project.Type): Project => ({
  ...fromV1Meta(p),
  wip: fromV1Wip(p.wip),
})

export const fromV1Agent = (a: V1.Agent): Agent => ({ ...fromV1Meta(a), mind: fromV1Mind(a.mind) })

export const fromV1Kpi = (k: typeof V1.Kpi.Type) => fromV1Meta(k)

/**
 * A JSON patch (ProjectUpdated / AreaUpdated `patch` and `before`): `wip.maxActiveInitiatives` → `wip.maxNow` inside;
 * everything else as stored.
 */
export const fromV1JsonPatch = <P extends Readonly<Record<string, unknown>>>(patch: P): P => {
  const wip = patch.wip
  if (wip === null || typeof wip !== "object" || !("maxActiveInitiatives" in wip)) return patch
  const { maxActiveInitiatives, ...rest } = wip as { readonly maxActiveInitiatives: unknown }
  return { ...patch, wip: { ...rest, maxNow: maxActiveInitiatives } }
}

export const fromV1WakeReason = (r: typeof V1.WakeReason.Type): WakeReason => {
  switch (r._tag) {
    case "assignment":
      return { _tag: "assignment", itemId: itemId(r.taskId) }
    case "run_ended":
      return {
        _tag: "run_ended",
        runId: r.runId,
        itemId: r.taskId === null ? null : itemId(r.taskId),
        outcome: r.outcome,
      }
    case "review_requested":
      return { _tag: "review_requested", itemId: itemId(r.taskId), round: r.round }
    case "blocker":
      return { _tag: "blocker", itemId: itemId(r.taskId), runId: r.runId }
    case "impossible_claimed":
      return { _tag: "impossible_claimed", itemId: itemId(r.taskId), runId: r.runId }
    case "area_request":
      return { _tag: "area_request", itemId: itemId(r.taskId) }
    case "intake":
      return { _tag: "triage", itemId: itemId(r.intakeId) }
    case "user_message":
    case "human_mention":
    case "ask":
    case "answer":
    case "proposal_resolved":
    case "kpi_threshold":
    case "monitor":
      return r
    default:
      return r satisfies never
  }
}

export const fromV1WakeBatch = (b: typeof V1.WakeBatch.Type): WakeBatch => ({
  wakeBatchId: b.wakeBatchId,
  reasons: [fromV1WakeReason(b.reasons[0]), ...b.reasons.slice(1).map(fromV1WakeReason)],
  coalescedFrom: b.coalescedFrom,
})

const fromV1LoadedItem = (l: typeof V1.LoadedItem.Type): typeof LoadedItem.Type => ({
  ...l,
  scope: fromV1MemoryScope(l.scope),
})

export const fromV1ContextPack = (p: typeof V1.ContextPack.Type): typeof ContextPack.Type => ({
  hash: p.hash,
  files: p.files,
  manifest: p.manifest.map(fromV1LoadedItem),
  ancestry: {
    goalIds: p.ancestry.goalIds,
    topItemId: p.ancestry.initiativeId === null ? null : itemId(p.ancestry.initiativeId),
    areaId: p.ancestry.areaId,
    itemId: p.ancestry.taskId === null ? null : itemId(p.ancestry.taskId),
  },
  handoff: p.handoff,
  taint: p.taint,
  tokenEstimate: p.tokenEstimate,
})
